import { intersection, lowerCase, omitBy, uniqueId } from 'lodash';

import { type AssetType, isImageAssetType } from 'src/data/asset';
import { CHANNEL, type Channel } from 'src/data/channel';
import { GRAPHQL_ERROR_CODES, type GraphQLErrorCode } from 'src/data/graphqlErrorCodes';
import { calculateImageDimensionsFile, type ImageDimensions } from 'src/lib/browser';
import type { ID } from 'src/lib/collection';
import { SLATE_TEXT_FORMATS, type SlateTextFormat } from 'src/lib/slate';

import { EMAILS_CHANNELS_AND_RESOURCES } from './const';
import type { CustomerResource, Resource } from './types';

export const GENERIC_RESOURCE = 'generic_resource';

export const EMAIL_CSV_RESOURCE = {
  emailSubjectLineCsvResource: 'email_subject_csv_resource',
  emailPromotionCsvResource: 'email_promotion_csv_resource',
  emailColdCsvResource: 'email_cold_csv_resource',
} as const;
export type EmailCsvResourceType = (typeof EMAIL_CSV_RESOURCE)[keyof typeof EMAIL_CSV_RESOURCE];

export const EMAIL_CSV_RESOURCES: EmailCsvResourceType[] = Object.values(EMAIL_CSV_RESOURCE);

export const isEmailCsvResourceDocument = (type: string): type is EmailCsvResourceType =>
  EMAIL_CSV_RESOURCES.includes(type as EmailCsvResourceType);

//TODO - CAN BE REMOVED?
export const RESOURCE_DOCUMENT = {
  ...EMAIL_CSV_RESOURCE,
  emailCsvResource: 'email_csv_resource',
  googleDocs: 'google_docs',
};
export type ResourceDocument = (typeof RESOURCE_DOCUMENT)[keyof typeof RESOURCE_DOCUMENT];

const RESOURCE_DOCUMENT_PROPS = {
  [RESOURCE_DOCUMENT.emailCsvResource]: {
    label: 'Email performance report',
  },
  [RESOURCE_DOCUMENT.emailSubjectLineCsvResource]: {
    label: 'Email - subject line performance report',
  },
  [RESOURCE_DOCUMENT.emailPromotionCsvResource]: {
    label: 'Email - promotion performance report',
  },
  [RESOURCE_DOCUMENT.emailColdCsvResource]: {
    label: 'Email - cold performance report',
  },
  [RESOURCE_DOCUMENT.googleDocs]: {
    label: 'Google docs',
  },
};

export const RESOURCE_TAG = {
  general: 'general',
  amazon: 'amazon',
  trustpilot: 'trustpilot',
  homepage: 'home_page',
  productPage: 'product_page',
  landingPage: 'landing_page',
  competitors: 'competitors',
  thoughtLeadership: 'thought_leadership',
  blogPost: 'blog_post',
  generalDocument: 'general_document',
  generalImage: 'general_image',
};
export type ResourceTag = (typeof RESOURCE_TAG)[keyof typeof RESOURCE_TAG];

export const RESOURCE_TAG_PROPS = {
  [RESOURCE_TAG.general]: {
    label: 'General',
  },
  [RESOURCE_TAG.amazon]: {
    label: 'Amazon reviews',
  },
  [RESOURCE_TAG.trustpilot]: {
    label: 'Trustpilot reviews',
  },
  [RESOURCE_TAG.homepage]: {
    label: 'Homepage',
  },
  [RESOURCE_TAG.productPage]: {
    label: 'Product information',
  },
  [RESOURCE_TAG.landingPage]: {
    label: 'Landing page',
  },
  [RESOURCE_TAG.competitors]: {
    label: 'Competitor',
  },
  [RESOURCE_TAG.thoughtLeadership]: {
    label: 'Thought leadership',
  },
  [RESOURCE_TAG.blogPost]: {
    label: 'Blog post',
  },
  [RESOURCE_TAG.generalDocument]: {
    label: 'General document',
  },
  [RESOURCE_TAG.generalImage]: {
    label: 'General image',
  },
};

export const getResourceTagLabel = (tag: ResourceTag): string => {
  return RESOURCE_TAG_PROPS[tag]?.label || tag;
};

export const RESOURCE_CONTENT_TYPE = {
  url: 'url',
  document: 'document',
  image: 'image',
  messagingBankAsset: 'messagingBankAsset',
  audio: 'audio',
  video: 'video',
} as const;
export type ResourceContentType =
  (typeof RESOURCE_CONTENT_TYPE)[keyof typeof RESOURCE_CONTENT_TYPE];

export const VISIBLE_RESOURCE_CONTENT_TYPES: ResourceContentType[] = [
  RESOURCE_CONTENT_TYPE.url,
  RESOURCE_CONTENT_TYPE.document,
  RESOURCE_CONTENT_TYPE.image,
];

const MAX_IMAGE_DIMENSION = 6_000;

export const RESOURCE_FILE_UPLOAD_ERROR_CODE = {
  fileTooLarge: 'file-too-large',
  fileInvalidType: 'file-invalid-type',
  fileAlreadyExists: 'file-already-exists',
  fileExistsButNotAssociatedToWorkspace: 'file-exists-but-not-associated-to-workspace',
  imgResolutionExceeded: 'img-resolution-exceeded',
  tooManyFiles: 'too-many-files',
  notEnoughContent: 'not-enough-content',
};
export type ResourceFileUploadErrorCode =
  (typeof RESOURCE_FILE_UPLOAD_ERROR_CODE)[keyof typeof RESOURCE_FILE_UPLOAD_ERROR_CODE];

const GRAPHQL_ERROR_TO_UPLOAD_ERROR_MAP: Record<string, ResourceFileUploadErrorCode> = {
  [GRAPHQL_ERROR_CODES.resourceFileAlreadyExist]: RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists,
  [GRAPHQL_ERROR_CODES.resourceFileNotEnoughContent]:
    RESOURCE_FILE_UPLOAD_ERROR_CODE.notEnoughContent,
};

export const graphQLErrorCodeToResourceUploadErrorCode = (
  errorCode: GraphQLErrorCode
): ResourceFileUploadErrorCode | undefined => {
  return GRAPHQL_ERROR_TO_UPLOAD_ERROR_MAP[errorCode];
};

type AcceptedFileType = Record<string, string[]>;
type ResourceContentTypeError = { title: string; description?: string };
interface ResourceContentTypeProps {
  label: string;
  nameInputPlaceholder: string;
  referToPlaceholder?: string;
  fileDescription?: string;
  fileMaxSize?: number;
  acceptedFileType?: AcceptedFileType;
  errors?: Record<ResourceFileUploadErrorCode, ResourceContentTypeError>;
}

const RESOURCE_CONTENT_TYPE_PROPS: Record<ResourceContentType, ResourceContentTypeProps> = {
  [RESOURCE_CONTENT_TYPE.url]: {
    label: 'Webpage',
    nameInputPlaceholder: 'Acme Homepage',
  },
  [RESOURCE_CONTENT_TYPE.document]: {
    label: 'File',
    nameInputPlaceholder: 'Enter name',
    referToPlaceholder: 'refer to this file',
    fileDescription: 'Supports PDF, DOCX, DOC, TXT & VVT files up to 50MB',
    fileMaxSize: 50_000_000,
    acceptedFileType: {
      'text/pdf': ['.pdf'],
      'text/vtt': ['.vtt'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/csv': ['.csv'],
    },
    errors: {
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge]: {
        title: 'File is too large for upload. The maximum allowed size is 50MB.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType]: {
        title: 'File type is not supported. Please upload a valid file.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists]: {
        title: 'Upload failed. The document already exists in this account.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace]: {
        title: 'Upload failed. This document already exists in another workspace.',
        description: 'Please go to the workspace settings to grant access and try again.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.notEnoughContent]: {
        title: "Upload failed. We couldn't find enough content in this file.",
      },
    },
  },
  [RESOURCE_CONTENT_TYPE.image]: {
    label: 'Image',
    nameInputPlaceholder: 'Enter name',
    referToPlaceholder: 'refer to this image',
    fileDescription: 'Max image size 20MB (JPEG, JPG, PNG only)',
    fileMaxSize: 20_000_000,
    acceptedFileType: {
      'image/jpg': ['.jpg'],
      'image/jpeg': ['.jpeg'],
      'image/png': ['.png'],
    },
    errors: {
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge]: {
        title: 'The uploaded image is too big. The maximum file size allowed is 20MB.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType]: {
        title: 'Upload failed. Only JPG, JPEG, and PNG formats are supported.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists]: {
        title: 'Upload failed. The image already exists in the workspace.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.imgResolutionExceeded]: {
        title: `Image upload failed. The maximum resolution supported is ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION} pixels.`,
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace]: {
        title: 'Upload failed. This image already exists in another workspace.',
        description: 'Please go to the workspace settings to grant access and try again.',
      },
    },
  },
  [RESOURCE_CONTENT_TYPE.messagingBankAsset]: {
    label: 'Messaging Bank Asset',
    nameInputPlaceholder: 'Enter name',
  },
  [RESOURCE_CONTENT_TYPE.audio]: {
    label: 'Audio',
    nameInputPlaceholder: 'Enter name',
    referToPlaceholder: 'refer to this audio',
    fileDescription: 'Supports MP3, WAV',
    fileMaxSize: 100_000_000,
    acceptedFileType: {
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
    },
    errors: {
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge]: {
        title: 'The uploaded audio is too big. The maximum file size allowed is 100MB.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType]: {
        title: 'Upload failed. Only MP3 and WAV formats are supported.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists]: {
        title: 'Upload failed. The audio already exists in the workspace.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace]: {
        title: 'Upload failed. This audio already exists in another workspace.',
        description: 'Please go to the workspace settings to grant access and try again.',
      },
    },
  },
  [RESOURCE_CONTENT_TYPE.video]: {
    label: 'Video',
    nameInputPlaceholder: 'Enter name',
    referToPlaceholder: 'refer to this video',
    fileDescription: 'Supports MP4, AVI, MOV, MPEG',
    fileMaxSize: 100_000_000,
    acceptedFileType: {
      'video/mp4': ['.mp4'],
      'video/x-msvideo': ['.avi'],
      'video/quicktime': ['.mov'],
      'video/mpeg': ['.mpeg'],
    },
    errors: {
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge]: {
        title: 'The uploaded video is too big. The maximum file size allowed is 100MB.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType]: {
        title: 'Upload failed. Only MP4, AVI, MOV and MPEG formats are supported.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists]: {
        title: 'Upload failed. The video already exists in the workspace.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace]: {
        title: 'Upload failed. This file already exists in another workspace.',
        description: 'Please go to the workspace settings to grant access and try again.',
      },
    },
  },
};

export const getResourceContentTypeLabel = (contentType: ResourceContentType): string => {
  return RESOURCE_CONTENT_TYPE_PROPS[contentType]?.label || contentType;
};

export const getResourceContentTypeNamePlaceholder = (contentType: ResourceContentType): string => {
  return RESOURCE_CONTENT_TYPE_PROPS[contentType]?.nameInputPlaceholder || contentType;
};

export const getResourceContentTypeReferToPlaceholder = (
  contentType: ResourceContentType
): string => {
  return RESOURCE_CONTENT_TYPE_PROPS[contentType]?.referToPlaceholder || '';
};

export const getResourceContentTypeAccept = (
  contentType: ResourceContentType,
  allowedFileTypes?: ResourceFileType[]
): AcceptedFileType => {
  const acceptedMimeTypesMap = RESOURCE_CONTENT_TYPE_PROPS[contentType]?.acceptedFileType || {};
  if (!Array.isArray(allowedFileTypes) || allowedFileTypes.length === 0) {
    return acceptedMimeTypesMap;
  }

  const allowedFileExtensions = allowedFileTypes.map(fileTypeToExtension);
  return omitBy(
    acceptedMimeTypesMap,
    (fileExtensions) => intersection(fileExtensions, allowedFileExtensions).length === 0
  );
};

export const getResourceContentTypeFileDescription = (
  contentType: ResourceContentType
): string | undefined => {
  return RESOURCE_CONTENT_TYPE_PROPS[contentType]?.fileDescription;
};

export const getResourceContentError = (
  contentType: ResourceContentType,
  errorCode: ResourceFileUploadErrorCode
): ResourceContentTypeError | undefined =>
  RESOURCE_CONTENT_TYPE_PROPS[contentType]?.errors?.[errorCode];

export const getResourceContentErrorMessage = (
  contentType: ResourceContentType,
  errorCode: ResourceFileUploadErrorCode
): string | undefined => {
  const error = getResourceContentError(contentType, errorCode);
  if (!error) {
    return;
  }

  return error.description ? `${error.title} ${error.description}` : error.title;
};

export const getResourceDocumentError = (
  errorCode: ResourceFileUploadErrorCode
): ResourceContentTypeError | undefined =>
  getResourceContentError(RESOURCE_CONTENT_TYPE.document, errorCode);
export const getResourceDocumentErrorMessage = (
  errorCode: ResourceFileUploadErrorCode
): string | undefined => getResourceContentErrorMessage(RESOURCE_CONTENT_TYPE.document, errorCode);

export const INCREASED_LARGE_RESOURCE_MAX_FILE_SIZE = 5_000_000_000; // 5GB

export const isLargeFileResourceType = (type: ResourceContentType): boolean =>
  ([RESOURCE_CONTENT_TYPE.video, RESOURCE_CONTENT_TYPE.audio] as ResourceContentType[]).includes(
    type
  );

export const getResourceFileMaxSize = (resourceType: ResourceContentType): number | undefined => {
  if (isLargeFileResourceType(resourceType)) {
    return INCREASED_LARGE_RESOURCE_MAX_FILE_SIZE;
  }
  return RESOURCE_CONTENT_TYPE_PROPS[resourceType]?.fileMaxSize;
};

export const getUploadingResourceHint = ({
  resourceType,
  fileSize,
}: {
  resourceType: ResourceContentType;
  fileSize?: number;
}): string => {
  const getMinutes = () => {
    switch (resourceType) {
      case RESOURCE_CONTENT_TYPE.video:
      case RESOURCE_CONTENT_TYPE.audio:
        return fileSize && fileSize > 500_000_000 ? 10 : 5; // 5 minutes for files up to 500MB, 10 minutes for files over 500MB

      default:
        return null;
    }
  };

  const minutes = getMinutes();
  let result = 'Your file is being uploaded and processed.';
  if (minutes) {
    result += `\nThis should take around ${minutes} minutes.`;
  }
  return result;
};

export const RESOURCE_STATUS = {
  active: 'active',
  processing: 'processing',
  archived: 'archived',
};
export type ResourceStatus = (typeof RESOURCE_STATUS)[keyof typeof RESOURCE_STATUS];

export const getResourceDocumentLabel = (document: ResourceDocument): string =>
  RESOURCE_DOCUMENT_PROPS[document]?.label;

export const getIsEmailChannel = (channel: Channel): boolean =>
  EMAILS_CHANNELS_AND_RESOURCES.includes(channel) || channel === CHANNEL.emailChannel;

export const getCreatedResourceMessage = (resource: CustomerResource): string => {
  if (resource.contentType === RESOURCE_CONTENT_TYPE.url) {
    return `A resource was successfully added to Anyword: "${resource?.content}"`;
  }

  return `A resource was successfully added to Anyword: "${resource?.name}"`;
};

export const getContentTypeTag = (contentType: ResourceContentType): string => {
  switch (contentType) {
    case RESOURCE_CONTENT_TYPE.document:
      return RESOURCE_TAG.generalDocument;
    case RESOURCE_CONTENT_TYPE.image:
      return RESOURCE_TAG.generalImage;
    default:
      return RESOURCE_TAG.general;
  }
};

export const resourceChannelToContentType = (channel: Channel): ResourceContentType => {
  if (channel === CHANNEL.other) {
    return RESOURCE_CONTENT_TYPE.url;
  }
  if (channel === CHANNEL.generalDocument) {
    return RESOURCE_CONTENT_TYPE.document;
  }
  return RESOURCE_CONTENT_TYPE.url;
};

export const isUrlResourceContentType = (contentType: ResourceContentType): boolean =>
  contentType === RESOURCE_CONTENT_TYPE.url;
export const isUrlResource = (resource: CustomerResource): boolean =>
  isUrlResourceContentType(resource?.contentType);

export const isImageResourceContentType = (contentType: ResourceContentType): boolean =>
  contentType === RESOURCE_CONTENT_TYPE.image;
export const isImageResource = (resource: CustomerResource): boolean =>
  isImageResourceContentType(resource?.contentType);

export const isDocumentResource = (resource: CustomerResource): boolean =>
  resource?.contentType === RESOURCE_CONTENT_TYPE.document;
export const isAudioResource = (resource: CustomerResource): boolean =>
  resource?.contentType === RESOURCE_CONTENT_TYPE.audio;
export const isVideoResource = (resource: CustomerResource): boolean =>
  resource?.contentType === RESOURCE_CONTENT_TYPE.video;

// If we have "max file size" value for this resource type, the we can treat it as a "file" resource
export const isFileResourceContentType = (contentType: ResourceContentType): boolean =>
  RESOURCE_CONTENT_TYPE_PROPS[contentType]?.fileMaxSize != null;
export const isFileResource = (resource: CustomerResource): boolean =>
  isFileResourceContentType(resource?.contentType);

export const isNonImageFileResourceContentType = (contentType: ResourceContentType): boolean =>
  isFileResourceContentType(contentType) && !isImageResourceContentType(contentType);
export const isNonImageFileResource = (resource: CustomerResource): boolean =>
  isNonImageFileResourceContentType(resource?.contentType);

export const RESOURCE_FILE_TYPE = {
  pdf: 'pdf',
  vtt: 'vtt',
  txt: 'txt',
  doc: 'doc',
  docx: 'docx',
  csv: 'csv',
};
export type ResourceFileType = (typeof RESOURCE_FILE_TYPE)[keyof typeof RESOURCE_FILE_TYPE];

export const isResourceFileTypePdf = (fileType: ResourceFileType): boolean =>
  lowerCase(fileType) === RESOURCE_FILE_TYPE.pdf;

export const fileTypeToExtension = (fileType: ResourceFileType): string => `.${fileType}`;

export const validateImageResourceDimensions = (dimensionsObj: ImageDimensions): boolean => {
  return Object.values(dimensionsObj).every((dimension) => dimension <= MAX_IMAGE_DIMENSION);
};

export const validateResourceFileType = (
  resource: CustomerResource,
  allowedFileTypes: ResourceFileType[]
): boolean => {
  if (!resource || !Array.isArray(allowedFileTypes) || allowedFileTypes.length === 0) {
    return false;
  }
  if (!resource.file?.fileType) {
    return false;
  }

  return allowedFileTypes.includes(resource.file.fileType);
};

export const RESOURCE_UPLOAD_ID_PREFIX = {
  image: 'uploadImage',
  document: 'uploadDocument',
};
export const generateResourceUploadId = (resourceType: ResourceContentType): string => {
  const prefix = isImageResourceContentType(resourceType)
    ? RESOURCE_UPLOAD_ID_PREFIX.image
    : RESOURCE_UPLOAD_ID_PREFIX.document;
  return `${prefix}-${uniqueId()}`;
};

export const isResourceUploadId = (id: ID): boolean => {
  if (!id) {
    return false;
  }
  const stringId = String(id);
  return (
    stringId.startsWith(RESOURCE_UPLOAD_ID_PREFIX.image) ||
    stringId.startsWith(RESOURCE_UPLOAD_ID_PREFIX.document)
  );
};

export const hasResourcesWithUploadId = (resources: CustomerResource[]): boolean =>
  resources.some((r) => isResourceUploadId(String(r.id)));

export const enrichResourceFileInfo = async ({
  resourceType,
  file,
}: {
  resourceType: ResourceContentType;
  file: File;
}) => {
  if (resourceType !== RESOURCE_CONTENT_TYPE.image) {
    return file;
  }

  const dimensions = await calculateImageDimensionsFile(file);

  const newFile = Object.assign(file, {
    preview: URL.createObjectURL(file),
    dimensions,
  });

  if (!validateImageResourceDimensions(newFile.dimensions)) {
    throw new Error(
      getResourceContentErrorMessage(
        resourceType,
        RESOURCE_FILE_UPLOAD_ERROR_CODE.imgResolutionExceeded
      )
    );
  }

  return newFile;
};

/**
 * Mapping of resource content types to corresponding Slate text formats.
 */
const RESOURCE_TYPE_TO_SLATE_TEXT_FORMAT: Record<ResourceContentType, SlateTextFormat> = {
  [RESOURCE_CONTENT_TYPE.image]: SLATE_TEXT_FORMATS.PROMPT_IMAGE,
  [RESOURCE_CONTENT_TYPE.document]: SLATE_TEXT_FORMATS.PROMPT_DOCUMENT,
  [RESOURCE_CONTENT_TYPE.messagingBankAsset]: SLATE_TEXT_FORMATS.BANK_MESSAGE,
  [RESOURCE_CONTENT_TYPE.url]: SLATE_TEXT_FORMATS.RESOURCE_URL,
  [RESOURCE_CONTENT_TYPE.audio]: SLATE_TEXT_FORMATS.PROMPT_AUDIO,
  [RESOURCE_CONTENT_TYPE.video]: SLATE_TEXT_FORMATS.PROMPT_VIDEO,
};

/**
 * Gets the Slate text format corresponding to a given resource content type.
 *
 * @param resourceType - The resource content type.
 * @returns - The corresponding Slate text format
 */
export const getSlateTextFormatFromResourceContentType = (
  resourceType: ResourceContentType
): SlateTextFormat => RESOURCE_TYPE_TO_SLATE_TEXT_FORMAT[resourceType];

/**
 * Prepares the list of visible resource content types, optionally including audio and video resources.
 *
 * @param shouldIncludeAudioVideoResources - Determines whether audio and video resources should be included.
 * @returns - An array of visible resource content types.
 */
export const getVisibleResourceContentTypes = (
  shouldIncludeAudioVideoResources: boolean
): ResourceContentType[] =>
  shouldIncludeAudioVideoResources
    ? [...VISIBLE_RESOURCE_CONTENT_TYPES, RESOURCE_CONTENT_TYPE.audio, RESOURCE_CONTENT_TYPE.video]
    : VISIBLE_RESOURCE_CONTENT_TYPES;

export const mimicImageResource = ({
  path,
  imageResourceId,
}: {
  path: string;
  imageResourceId?: ID;
}): (Resource & { id?: ID }) | undefined => {
  if (!path) {
    return undefined;
  }

  return {
    id: imageResourceId,
    contentType: RESOURCE_CONTENT_TYPE.image,
    file: {
      originalUrl: path,
    },
  };
};

export interface BaseAssetExtractedFromResource {
  assetType: AssetType;
}

export interface TextAssetExtractedFromResource extends BaseAssetExtractedFromResource {
  content: string;
}

export interface ImageAssetExtractedFromResource extends BaseAssetExtractedFromResource {
  imageResourceId: ID;
  path: string;
}

export type AssetExtractedFromResource =
  | TextAssetExtractedFromResource
  | ImageAssetExtractedFromResource;

export const isImageAssetExtractedFromResource = (
  asset: AssetExtractedFromResource
): asset is ImageAssetExtractedFromResource => {
  return isImageAssetType(asset.assetType);
};

export const isTextAssetExtractedFromResource = (
  asset: AssetExtractedFromResource
): asset is TextAssetExtractedFromResource => {
  return !isImageAssetType(asset.assetType);
};
